/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { OpenApiRouteResultType } from '../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../mixins/translate/translate';
import { IStoreState } from '../../store/IStoreState';
import { openApiFetch } from '../../store/openApiFetch';
import { PopupElement } from '../elements';
import { ChatMessage, IChatSource } from './ChatMessage';
import { ChatProgressIndicator } from './ChatProgressIndicator';

type ConversationLookupResult = OpenApiRouteResultType<'/api/ai/chat/contexts/{contextType}/{contextId}/conversation', 'get'>;
type GeneralConversationLookupResult = OpenApiRouteResultType<'/api/ai/chat/general/conversation', 'get'>;
type MessagesListResult = OpenApiRouteResultType<'/api/ai/chat/conversations/{conversationId}/messages', 'get'>;

interface IObjectOrSurveyProps {
    contextType: 'object' | 'survey';
    contextId: string;
    // Display name of the object/survey for the panel header.
    contextLabel: string;
}

interface IGeneralProps {
    contextType: 'general';
    // contextId/contextLabel intentionally absent — general chat is
    // tenant-wide and uses tools instead of pre-assembled context.
}

type ChatPanelProps = (IObjectOrSurveyProps | IGeneralProps) & {
    isOpen: boolean;
    onClose: () => void;
};

interface IDisplayMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    sources?: IChatSource[] | undefined;
    isInterrupted?: boolean | undefined;
}

const maxMessagesPerConversation = 50;

let messageCounter = 0;
function generateLocalId(): string {
    messageCounter += 1;
    return `local-${Date.now()}-${messageCounter}`;
}

interface ISseEventChunk {
    type: 'chunk';
    text: string;
}
interface ISseEventProgress {
    type: 'progress';
    kind: 'rotate' | 'tool';
    text: string;
    tool: string | null;
}
interface ISseEventSources {
    type: 'sources';
    sources: IChatSource[];
}
interface ISseEventConversation {
    type: 'conversation';
    conversationId: string;
}
interface ISseEventError {
    type: 'error';
    message: string;
}
type SseEvent = ISseEventChunk | ISseEventProgress | ISseEventSources | ISseEventConversation | ISseEventError;

function parseSseEvent(line: string): SseEvent | null {
    if (!line.startsWith('data: ')) {
        return null;
    }
    const payload = line.slice(6).trim();
    if (!payload || payload === '[DONE]') {
        return null;
    }
    try {
        const parsed = JSON.parse(payload) as SseEvent;
        return parsed;
    } catch {
        // Partial payload (event split across reads); buffer + retry.
        return null;
    }
}

// Yields parsed SSE events. Handles one read containing multiple
// events or one event spanning multiple reads.
async function* readSseStream(response: Response): AsyncGenerator<SseEvent> {
    const reader = response.body?.getReader();
    if (!reader) {
        return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { done: isDone, value } = await reader.read();
        if (isDone) {
            break;
        }
        buffer += decoder.decode(value, { stream: true });
        // Events end on a blank line per SSE spec.
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
            for (const line of part.split('\n')) {
                const event = parseSseEvent(line);
                if (event) {
                    yield event;
                }
            }
        }
    }
}

export function ChatPanel(props: ChatPanelProps): JSX.Element | null {
    // Stable values for hook dep arrays — `'contextId' in props ? … : null`
    // is a complex expression and trips react-hooks/exhaustive-deps.
    const propsContextId = 'contextId' in props ? props.contextId : null;
    const propsContextType = props.contextType;
    const translate = useTranslate();
    const userFirstName = useSelector((state: IStoreState) => state.authentication.firstName);
    const [messages, setMessages] = useState<IDisplayMessage[]>([]);
    const [input, setInput] = useState('');
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progressMessage, setProgressMessage] = useState<string | null>(null);
    // Set on a 409 reply so the cap UI shows even when local state
    // (which can be stale after a reload) would still allow a submit.
    const [hasReachedServerCap, setHasReachedServerCap] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const scrollEndRef = useRef<HTMLDivElement>(null);
    // For auto-focus after streaming ends (see useEffect below).
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const progressMessages = (translate('chat.progressMessages') as unknown as string[]) ?? [];

    // Restore any existing conversation for this (user, context) on open.
    useEffect(() => {
        if (!props.isOpen) {
            return undefined;
        }
        setHasReachedServerCap(false);
        let isCancelled = false;
        void (async () => {
            try {
                let conversation: { id: string } | null;
                if (propsContextType === 'general') {
                    const { conversation: c }: GeneralConversationLookupResult = await openApiFetch(
                        '/api/ai/chat/general/conversation',
                        'get',
                        {},
                        undefined as never,
                    );
                    conversation = c;
                } else {
                    const { conversation: c }: ConversationLookupResult = await openApiFetch(
                        '/api/ai/chat/contexts/{contextType}/{contextId}/conversation',
                        'get',
                        { contextType: propsContextType, contextId: propsContextId! },
                        undefined as never,
                    );
                    conversation = c;
                }
                if (isCancelled) {
                    return;
                }
                if (!conversation) {
                    setConversationId(null);
                    setMessages([]);
                    return;
                }
                setConversationId(conversation.id);
                const messagesResponse: MessagesListResult = await openApiFetch(
                    '/api/ai/chat/conversations/{conversationId}/messages',
                    'get',
                    { conversationId: conversation.id },
                    undefined as never,
                );
                if (isCancelled) {
                    return;
                }
                setMessages(
                    messagesResponse.items.map(
                        (m): IDisplayMessage => ({
                            id: m.id,
                            role: m.role === 'assistant' ? 'assistant' : 'user',
                            content: m.content,
                            sources: (m.sources ?? []) as IChatSource[],
                            isInterrupted: m.interrupted,
                        }),
                    ),
                );
            } catch {
                // Non-fatal — start fresh.
                setConversationId(null);
                setMessages([]);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [props.isOpen, propsContextType, propsContextId]);

    // Cancel any in-flight stream when the panel unmounts or closes.
    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isStreaming]);

    // Auto-focus the input on open and after streaming ends. rAF
    // defers until React has committed and the textarea is enabled.
    useEffect(() => {
        if (props.isOpen && !isStreaming) {
            const handle = requestAnimationFrame(() => inputRef.current?.focus());
            return () => cancelAnimationFrame(handle);
        }
        return undefined;
    }, [props.isOpen, isStreaming]);

    const startNewChat = useCallback(() => {
        abortRef.current?.abort();
        setConversationId(null);
        setMessages([]);
        setError(null);
        setProgressMessage(null);
        setHasReachedServerCap(false);
    }, []);

    const sendMessage = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || isStreaming) {
            return;
        }
        const userBubbleId = generateLocalId();
        const assistantBubbleId = generateLocalId();
        setMessages((prev) => [
            ...prev,
            { id: userBubbleId, role: 'user', content: trimmed },
            { id: assistantBubbleId, role: 'assistant', content: '' },
        ]);
        setInput('');
        setError(null);
        setProgressMessage(null);
        setIsStreaming(true);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const url =
                propsContextType === 'general'
                    ? '/api/ai/chat/general/messages'
                    : `/api/ai/chat/contexts/${propsContextType}/${propsContextId!}/messages`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ conversationId, message: trimmed }),
                signal: controller.signal,
                credentials: 'include',
            });
            if (!response.ok) {
                // 409 = MAX_MESSAGES_REACHED. Roll back optimistic
                // bubbles and show the cap banner instead of a raw "HTTP 409".
                if (response.status === 409) {
                    setMessages((prev) => prev.filter((m) => m.id !== userBubbleId && m.id !== assistantBubbleId));
                    setHasReachedServerCap(true);
                    return;
                }
                // Surface the server's structured error code so misconfig
                // (e.g. AI_CHAT_NOT_ENABLED) is diagnosable from the chat
                // UI rather than only from server logs.
                let body: { code?: string; message?: string } | null = null;
                try {
                    body = (await response.json()) as { code?: string; message?: string };
                } catch {
                    /* not JSON — leave body null */
                }
                // 404 NOT_FOUND or 403 CONTEXT_MISMATCH on a re-used
                // conversationId means our local id is stale (the row
                // was deleted or context changed). Drop the conversation
                // so the next send creates a new one — without this the
                // panel gets stuck and the user has to click "Nieuwe chat".
                if ((response.status === 404 || (response.status === 403 && body?.code === 'CONTEXT_MISMATCH')) && conversationId) {
                    setConversationId(null);
                }
                let detail = '';
                if (body?.code) {
                    detail = ` (${body.code})`;
                }
                if (body?.message) {
                    detail += `: ${body.message}`;
                }
                throw new Error(`HTTP ${response.status}${detail}`);
            }

            let assistantText = '';
            let hasReceivedFirstChunk = false;
            for await (const event of readSseStream(response)) {
                if (event.type === 'progress') {
                    setProgressMessage(event.text);
                } else if (event.type === 'chunk') {
                    if (!hasReceivedFirstChunk) {
                        hasReceivedFirstChunk = true;
                        setProgressMessage(null);
                    }
                    assistantText += event.text;
                    setMessages((prev) => prev.map((m) => (m.id === assistantBubbleId ? { ...m, content: assistantText } : m)));
                } else if (event.type === 'sources') {
                    setMessages((prev) => prev.map((m) => (m.id === assistantBubbleId ? { ...m, sources: event.sources } : m)));
                } else if (event.type === 'conversation') {
                    // Server tells us the freshly-created conversation id
                    // so subsequent turns reuse it instead of spawning a new one.
                    setConversationId(event.conversationId);
                } else if (event.type === 'error') {
                    setError(event.message);
                }
            }
        } catch (err) {
            if ((err as { name?: string }).name === 'AbortError') {
                setMessages((prev) => prev.map((m) => (m.id === assistantBubbleId ? { ...m, isInterrupted: true } : m)));
            } else {
                // Drop the empty optimistic bubble so the error
                // banner isn't accompanied by a blank "AI" message.
                setMessages((prev) => prev.filter((m) => m.id !== assistantBubbleId));
                setError(err instanceof Error ? err.message : translate('ai.errorOccurred'));
            }
        } finally {
            setIsStreaming(false);
            setProgressMessage(null);
            abortRef.current = null;
        }
    }, [conversationId, input, isStreaming, propsContextId, propsContextType, translate]);

    const stop = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void sendMessage();
            }
        },
        [sendMessage],
    );

    if (!props.isOpen) {
        return null;
    }

    // Either local count or a 409 refusal flips the cap. Local can be
    // stale after a history reload; the server signal only fires post-submit.
    const hasReachedCap = hasReachedServerCap || messages.length >= maxMessagesPerConversation;

    return (
        <PopupElement onClick={props.onClose}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '80vh',
                    maxHeight: 800,
                }}
            >
                <div
                    style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e5e5e5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <h4 style={{ margin: 0 }}>
                        <i className="fa fa-comment" style={{ marginRight: 8 }} />
                        {props.contextType === 'general'
                            ? translate('chat.chatGeneral')
                            : props.contextType === 'survey'
                              ? translate('chat.chatAboutSurvey').replace('{0}', props.contextLabel)
                              : translate('chat.chatAboutObject').replace('{0}', props.contextLabel)}
                    </h4>
                    <div>
                        {conversationId && (
                            <button type="button" className="btn btn-default btn-sm" onClick={startNewChat} style={{ marginRight: 8 }}>
                                <i className="fa fa-refresh" /> {translate('chat.startNewChat')}
                            </button>
                        )}
                        <button type="button" className="btn btn-default btn-sm" onClick={props.onClose}>
                            <i className="fa fa-times" />
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 12, backgroundColor: '#fafafa' }}>
                    {messages.length === 0 && !isStreaming && (
                        // Local-only greeting: not persisted, not sent to the
                        // model. Disappears as soon as the first real message
                        // is rendered (messages.length > 0). Rendered as a
                        // bold heading rather than a chat bubble — it's a
                        // welcome line, not a turn from the assistant.
                        <div
                            style={{
                                padding: '24px 12px',
                                fontSize: 22,
                                fontWeight: 600,
                                color: '#333',
                                textAlign: 'center',
                            }}
                        >
                            {userFirstName ? translate('chat.greeting').replace('{0}', userFirstName) : translate('chat.greetingNoName')}
                        </div>
                    )}
                    {messages.map((message) => (
                        <ChatMessage
                            key={message.id}
                            role={message.role}
                            content={message.content}
                            sources={message.sources}
                            isInterrupted={message.isInterrupted}
                            youLabel={translate('chat.you')}
                            aiLabel={translate('chat.assistant')}
                            sourcesLabel={translate('chat.sourcesLabel')}
                            interruptedLabel={translate('chat.interruptedLabel')}
                        />
                    ))}
                    {isStreaming && <ChatProgressIndicator messages={progressMessages} serverMessage={progressMessage} />}
                    {error && (
                        <div className="alert alert-danger" style={{ margin: '8px 0' }}>
                            {error}
                        </div>
                    )}
                    {hasReachedCap && (
                        <div className="alert alert-warning" style={{ margin: '8px 0' }}>
                            {translate('chat.maxMessagesReached')}
                            <div style={{ marginTop: 6 }}>
                                <button type="button" className="btn btn-primary btn-sm" onClick={startNewChat}>
                                    {translate('chat.startNewChat')}
                                </button>
                            </div>
                        </div>
                    )}
                    <div ref={scrollEndRef} />
                </div>

                <div style={{ padding: 12, borderTop: '1px solid #e5e5e5', backgroundColor: '#fff' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <textarea
                            ref={inputRef}
                            className="form-control"
                            rows={2}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={translate('chat.placeholder')}
                            disabled={isStreaming || hasReachedCap}
                            style={{ resize: 'vertical', flex: 1 }}
                        />
                        {isStreaming ? (
                            <button type="button" className="btn btn-default" onClick={stop} style={{ minWidth: 80 }}>
                                <i className="fa fa-stop" /> {translate('chat.stop')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={sendMessage}
                                disabled={!input.trim() || hasReachedCap}
                                style={{ minWidth: 80 }}
                            >
                                {translate('chat.send')}
                            </button>
                        )}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#888', fontStyle: 'italic' }}>{translate('chat.disclaimer')}</div>
                </div>
            </div>
        </PopupElement>
    );
}
